import cn from '../../../../../utils/cn';
import PropTypes from 'prop-types';
import Divider from '../../../Divider/Divider';
import DraftEditorField from '../../../../Form/DraftEditorField/DraftEditorField';
import ExpandableContainer from '../../../ExpandableContainer/ExpandableContainer';
import LabelTextInline from '../../../../utils/LabelTextInline/LabelTextInline';
import { FormattedMessage } from 'react-intl';
import stateEnums from '../../../../utils/Programs/stateEnums';
import ageRangeConverters from '../../../../utils/Ages/ageRangeConverters';
import { isEligTextEmpty } from '../../../../utils/Programs/checkEmptyText';

const ProgramEligibility = (props) => {
  const {
    className,
    program: {
      accessibility_options,
      delivery_options,
      description,
      eligibility_text,
      languages,
      payment_options,
      population_specializations,
      population_restrictions,
      transportation_options,
      age_requirements,
      age_specializations,
      requirements,
      postal_code_requirements,
      state_requirements,
      county_requirements,
      city_requirements,
    },
    showLess,
    showMore,
  } = props;

  const { processAgeText, convertApiAgeRangeToUnits } = ageRangeConverters;

  const countyRequirments = county_requirements?.map(r => r.name.concat(', ', r.state)).join('; ');
  const cityRequirements = city_requirements?.map(r => r.name.concat(', ', r.state)).join('; ');
  const postalCodeRequirements = postal_code_requirements?.map(r => r.postal_code);

  const transformedStateRequirements = state_requirements?.map(s => stateEnums.states[s.id]);
  const transformedAgeRequirements = age_requirements ?
    processAgeText(...convertApiAgeRangeToUnits(age_requirements)) : null;
  const transformedAgeSpecializations = age_specializations ?
    processAgeText(...convertApiAgeRangeToUnits(age_specializations)) : null;
  const genderRequirements = requirements?.filter(r => r.category === 'gender')?.map(r => r.display_name);

  const hasRegionalElig = Boolean([
    ...(postal_code_requirements || []),
    ...(state_requirements || []),
    ...(county_requirements || []),
    ...(city_requirements || []),
  ].length !== 0);

  const eligTextEmpty = isEligTextEmpty(eligibility_text);

  return (
    <div className={cn('text-text-blue', className)} >
      <div className={cn({ hidden: eligTextEmpty })}>
        <ExpandableContainer
          collapsedHeight="70px"
          id="program-eligibility-expandable"
          controlled
          isOpen={showMore}
          hideToggle
          showGradient={showLess}
        >
          <h4 className="mb-2 text-sm font-medium font-heavy-font">
            <FormattedMessage
              id="Tl36ae"
              description="eligibility"
              defaultMessage="Eligibility"
            />
          </h4>
          <DraftEditorField
            hideLabel
            id="program-eligibility_text"
            label={<FormattedMessage
              id="Tl36ae"
              description="eligibility"
              defaultMessage="Eligibility"
            />}
            readOnly
            value={eligibility_text || ''}
          />
        </ExpandableContainer>
      </div>

      {/* Adding additional expandable when eligibility DNE */}

      {
        eligTextEmpty && description && showLess ? (
          <ExpandableContainer
            collapsedHeight="70px"
            id="program-eligibility-expandable"
            controlled
            isOpen={showMore}
            hideToggle
            showGradient={showLess}
          >
            <h4 className="mb-4">
              <FormattedMessage
                id="rT7jXR"
                description="description"
                defaultMessage="Description"
              />
            </h4>
            <DraftEditorField
              label={<FormattedMessage
                id="rT7jXR"
                description="description"
                defaultMessage="Description"
              />}
              hideLabel
              id="program-description-text"
              value={description}
              readOnly
            />
          </ExpandableContainer>
        ) : null
      }

      {
        hasRegionalElig && showMore ? (
          <div>
            {eligTextEmpty ?
              null : (<Divider />)}

            <h4 className="mb-4">
              <FormattedMessage
                id="jwwDck"
                description="clients-must-live-text"
                defaultMessage="Client must live in the following..."
              />
            </h4>
            {
              transformedStateRequirements && (
                <LabelTextInline
                  className="mb-4"
                  label={<FormattedMessage
                    id="XW82Wp"
                    description="states"
                    defaultMessage="States"
                  />}
                  content={transformedStateRequirements}
                />
              )
            }
            {
              county_requirements?.length > 0 && (
                <LabelTextInline
                  className="mb-4"
                  label={<FormattedMessage
                    id="mZ2nD1"
                    description="counties"
                    defaultMessage="Counties"
                  />}
                  content={countyRequirments}
                />
              )
            }
            {
              city_requirements?.length > 0 && (
                <LabelTextInline
                  className="mb-4"
                  label={<FormattedMessage
                    id="Zonjcr"
                    description="cities"
                    defaultMessage="Cities"
                  />}
                  content={cityRequirements}
                />
              )
            }
            {
              postal_code_requirements?.length > 0 && (
                <LabelTextInline
                  className="mb-4"
                  label={<FormattedMessage
                    id="aDv4mN"
                    description="zip-codes"
                    defaultMessage="Zip codes"
                  />}
                  content={postalCodeRequirements}
                />
              )
            }
          </div>
        ) : null
      }

      {
        showMore && (
          <div>
            {(!eligTextEmpty || hasRegionalElig) ?
              (<Divider className="my-5" />) : null}
            <LabelTextInline
              className="mb-4 text-sm flex"
              label={<FormattedMessage
                id="zPsx0I"
                description="populations-restriction"
                defaultMessage="Populations restricted to"
              />}
              content={population_restrictions}
            />

            <LabelTextInline
              className="mb-4 text-sm flex"
              label={<FormattedMessage
                id="sUXZue"
                description="gender-requirements"
                defaultMessage="Genders restricted to"
              />}
              content={genderRequirements}
            />

            <LabelTextInline
              className="mb-4 text-sm flex"
              label={<FormattedMessage
                id="xByfh4"
                description="age-requirements"
                defaultMessage="Ages restricted to"
              />}
              content={transformedAgeRequirements}
            />

            <LabelTextInline
              className="mb-4 text-sm flex"
              label={<FormattedMessage
                id="o0u6lY"
                description="population-catered"
                defaultMessage="Populations specialized in"
              />}
              content={population_specializations}
            />

            <LabelTextInline
              className="mb-4 text-sm flex"
              label={<FormattedMessage
                id="XKOAjP"
                description="age-specialization"
                defaultMessage="Ages specialized in"
              />}
              content={transformedAgeSpecializations}
            />

            <LabelTextInline
              className="mb-4 text-sm flex"
              label={<FormattedMessage
                id="RqjsZm"
                description="languages"
                defaultMessage="Languages"
              />}
              content={languages}
            />

            <LabelTextInline
              className="mb-4 text-sm flex"
              label={<FormattedMessage
                id="lpYuHX"
                description="transportation"
                defaultMessage="Transportation"
              />}
              content={transportation_options}
            />

            <LabelTextInline
              className="mb-4 text-sm flex"
              label={<FormattedMessage
                id="J9KLw4"
                description="accessibility"
                defaultMessage="Accessibility"
              />}
              content={accessibility_options}
            />

            <LabelTextInline
              className="mb-4 text-sm flex"
              label={<FormattedMessage
                id="kCUBP8"
                description="primary-service-delivery"
                defaultMessage="Primary Method of Service Delivery"
              />}
              content={delivery_options}
            />

            <LabelTextInline
              className="mb-4 text-sm flex"
              label={<FormattedMessage
                id="MIPeJV"
                description="payment-options"
                defaultMessage="Payment options"
              />}
              content={payment_options}
            />
          </div>
        )
      }
    </div>
  );
};

ProgramEligibility.propTypes = {
  className: PropTypes.string,
  showMore: PropTypes.bool,
  showLess: PropTypes.bool,
  program: PropTypes.object.isRequired,
};

ProgramEligibility.defaultProps = {
  className: '',
  showLess: false,
  showMore: false,
};

export default ProgramEligibility;
